import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
    ArrowUpCircle, ArrowDownCircle, Search, Filter, Package, FileText
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function MovimentacaoHistorico({ movimentacoes = [], insumos = [] }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterTipo, setFilterTipo] = useState("todos");

    const formatCurrency = (value) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

    const getInsumoNome = (insumoId) => {
        const insumo = insumos.find(i => i.id === insumoId);
        return insumo?.nome || "Insumo removido";
    };

    const getInsumoUnidade = (insumoId) => {
        const insumo = insumos.find(i => i.id === insumoId);
        return insumo?.unidade || "";
    };

    const filtered = movimentacoes
        .filter(m => {
            const matchSearch = !searchTerm ||
                getInsumoNome(m.insumo_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
                m.observacao?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchTipo = filterTipo === "todos" || m.tipo === filterTipo;
            return matchSearch && matchTipo;
        })
        .sort((a, b) => new Date(b.data || b.created_at) - new Date(a.data || a.created_at));

    const totalEntradas = movimentacoes
        .filter(m => m.tipo === "entrada")
        .reduce((acc, m) => acc + (m.custo_total || 0), 0);

    const totalSaidas = movimentacoes
        .filter(m => m.tipo === "saida")
        .reduce((acc, m) => acc + (m.custo_total || 0), 0);

    const qtdEntradas = movimentacoes.filter(m => m.tipo === "entrada").length;
    const qtdSaidas = movimentacoes.filter(m => m.tipo === "saida").length;

    return (
        <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-600 text-sm font-medium">Total Movimentações</p>
                                <p className="text-2xl font-bold text-blue-800 mt-1">{movimentacoes.length}</p>
                            </div>
                            <Package className="w-10 h-10 text-blue-300" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-600 text-sm font-medium">Entradas ({qtdEntradas})</p>
                                <p className="text-2xl font-bold text-green-800 mt-1">{formatCurrency(totalEntradas)}</p>
                            </div>
                            <ArrowUpCircle className="w-10 h-10 text-green-300" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-red-600 text-sm font-medium">Saídas ({qtdSaidas})</p>
                                <p className="text-2xl font-bold text-red-800 mt-1">{formatCurrency(totalSaidas)}</p>
                            </div>
                            <ArrowDownCircle className="w-10 h-10 text-red-300" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl border border-stone-200 p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <Input
                            placeholder="Buscar por insumo ou observação..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <Select value={filterTipo} onValueChange={setFilterTipo}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="entrada">Entradas</SelectItem>
                            <SelectItem value="saida">Saídas</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-stone-50">
                                <TableHead className="w-[100px]">Data</TableHead>
                                <TableHead>Insumo</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead className="text-right">Quantidade</TableHead>
                                <TableHead className="text-right">Custo</TableHead>
                                <TableHead>Observação</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((m) => (
                                <TableRow key={m.id} className="hover:bg-stone-50 transition-colors">
                                    <TableCell className="font-medium text-stone-700">
                                        {m.data
                                            ? format(parseISO(m.data), "dd/MM/yy")
                                            : m.created_at
                                                ? format(parseISO(m.created_at), "dd/MM/yy")
                                                : "-"}
                                    </TableCell>
                                    <TableCell>
                                        <div className="font-medium text-stone-800">{getInsumoNome(m.insumo_id)}</div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant="outline"
                                            className={
                                                m.tipo === "entrada"
                                                    ? "bg-green-100 text-green-700 border-green-200"
                                                    : "bg-red-100 text-red-700 border-red-200"
                                            }
                                        >
                                            {m.tipo === "entrada" ? (
                                                <><ArrowUpCircle className="w-3 h-3 mr-1" /> Entrada</>
                                            ) : (
                                                <><ArrowDownCircle className="w-3 h-3 mr-1" /> Saída</>
                                            )}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className={`text-right font-bold ${m.tipo === "entrada" ? "text-green-600" : "text-red-600"}`}>
                                        {m.tipo === "entrada" ? "+" : "-"}{m.quantidade} {getInsumoUnidade(m.insumo_id)}
                                    </TableCell>
                                    <TableCell className="text-right text-stone-600">
                                        {formatCurrency(m.custo_total)}
                                    </TableCell>
                                    <TableCell className="text-sm text-stone-500 max-w-[250px] truncate">
                                        {m.observacao || "-"}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12 text-stone-400">
                                        <Package className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                                        <p>Nenhuma movimentação encontrada</p>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}
